import type { FC } from 'react';
import { useState, useRef } from 'react';
import { Tabs } from '@interstate/components/Tabs';
import { useDispatch, useSelector } from 'react-redux';

import { TemplateFormContainer } from './saveTemplate/components/TemplateFormContainer';
import { TEST_IDS, Tab } from './constants';
import ManageTemplatesWrapper from './manageTemplate/ManageTemplatesWrapper';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import CantBuildTemplateModal from '../cantBuildTemplateModal/CantBuildTemplateModal';

type TemplateSaveDrawerContentProps = {
    handleCancel: () => void;
    defaultDrawerTab: Tab;
};
const TemplateSaveDrawerContent: FC<TemplateSaveDrawerContentProps> = ({ handleCancel, defaultDrawerTab }) => {
    const [activeTab, setActiveTab] = useState(defaultDrawerTab);
    const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>(undefined);
    const [showCantBuildModal, setShowCantBuildModal] = useState(false);
    const wasTemplateRecentlySaved = useRef(false);
    const dispatch = useDispatch();
    const hasMixedVehicles = useSelector(dealTemplateSelectors.getHasMultipleVehiclesAcrossScenarios);
    const handleEditTemplate = (templateId: string) => {
        setEditingTemplateId(templateId);
        setActiveTab(Tab.Save);
    };

    const tabs = [
        {
            component: (
                <ManageTemplatesWrapper
                    wasTemplateRecentlySavedRef={wasTemplateRecentlySaved}
                    handleEditTemplate={handleEditTemplate}
                    isActive={activeTab === Tab.Manage}
                />
            ),
            label: 'Manage Existing Templates',
            value: Tab.Manage,
            'data-testid': Tab.Manage,
            active: activeTab === Tab.Manage
        },
        {
            component: (
                <TemplateFormContainer
                    editingTemplateId={editingTemplateId}
                    setEditingTemplateId={setEditingTemplateId}
                    handleCancel={handleCancel}
                    handleSaveTemplate={() => {
                        wasTemplateRecentlySaved.current = true;
                        setActiveTab(Tab.Manage);
                    }}
                    handleUpdateTemplate={() => {
                        wasTemplateRecentlySaved.current = false;
                        setActiveTab(Tab.Manage);
                    }}
                    isActive={activeTab === Tab.Save}
                />
            ),
            label: 'Save Templates',
            value: Tab.Save,
            'data-testid': Tab.Save,
            active: activeTab === Tab.Save
        }
    ];
    return (
        <>
            <Tabs
                data-testid={TEST_IDS.TEMPLATE_SAVE_DRAWER_TABS}
                id={TEST_IDS.TEMPLATE_SAVE_DRAWER_TABS}
                onActivate={(tab) => {
                    if (tab === Tab.Save && hasMixedVehicles) {
                        setShowCantBuildModal(true);
                        return;
                    }
                    setActiveTab(tab);
                    if (editingTemplateId) {
                        setEditingTemplateId(undefined);
                    }
                    if (tab === Tab.Save) {
                        wasTemplateRecentlySaved.current = false;

                        if (typeof dealTemplateActionCreators.dropUpdateDealTemplateData === 'function') {
                            dispatch(dealTemplateActionCreators.dropUpdateDealTemplateData());
                        }
                    }
                }}
                tabs={tabs}
            />
            <CantBuildTemplateModal show={showCantBuildModal} onHide={() => setShowCantBuildModal(false)} />
        </>
    );
};

export default TemplateSaveDrawerContent;
